/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

// tslint:disable:variable-name

import { ID, WithDirtyType, WithVersion } from '@gh/common-shared-data';
import { createEnum, EnumSet, Quantity, SelectionModel, WithOptimisticLock } from '@gh/core-data';
import { FormulaDto } from '../../common/formula.dto';
import { GtoUom } from '../../take-off-params/top.dto';

export enum FormulaPropagationMode { All = 'All', Selected = 'Selected' }

export const HpeInitType = createEnum('HOUSE_PLAN_ESTIMATE_INIT_TYPE', {
  FromScratch: 'NEW',
  CopyCostCenters: 'COPY_CC',
  CloneFromHousePlan: 'CLONE_FROM_HP',
  CloneFromJob: 'CLONE_FROM_JOB',
  Open: 'OPEN',
});
export type HpeInitType = EnumSet<typeof HpeInitType>;

export const HpeViewMode = createEnum('HOUSE_PLAN_ESTIMATE_VIEW_MODE', {
  Create: 'CREATE',
  Edit: 'EDIT',
  View: 'VIEW',
});
export type HpeViewMode = EnumSet<typeof HpeViewMode>;

export type HpeDetailsInitDto = {
  housePlanId: ID;
  initType: HpeInitType;
  sourceHousePlanId?: ID;
  sourceJobId?: ID;
};

export type HpeUomRefItemDto = {
  uomId: ID;
  code: string;
  usePieces: boolean;
  dimension1?: string;
  dimension2?: string;
  dimension3?: string;
};

export type HpeCostCenterItemRefDto = {
  hpItemId: ID;
  itemId: ID;
  topIds: ID[];
  code: string;
  uomId?: ID;
  formula?: FormulaDto;
  oldFormula?: FormulaDto;
  cuttingList?: HpeCuttingListItemDto[];
  memo?: string;
  hpComponentId?: ID;
};

export type HpeCostCenterRefDto = WithVersion<{
  costCenterId: ID;
  code: number;
  name: string;
  items: HpeCostCenterItemRefDto[];
  active: boolean;
  deleted: boolean;
  cost?: number;
  memo?: string;
  hpCostCenterId?: ID;
}>;

export type HpeTopRefItemDto = {
  topId: ID;
  code: string;
  gtoUom: GtoUom;
  isActive: boolean;
  isCommon: boolean;
};

export type HpeTopDto = {
  topId: ID;
  count: number;
  isFromGto: boolean;
  value: number;
};

export type HpeCuttingListItemDto = {
  sortOrder: number;
  description: string;
  quantity: number;
  pieces?: number;
  dimension1Value?: number;
  dimension2Value?: number;
  dimension3Value?: number;
};

export type HpeComponentItemDto = {
  hpComponentId: ID;
  costCenterId: ID;
  uomId: ID;
  itemId: ID;
  code: string;
  name: string;
  cost: number;
  extension: number;
  quantity: number;
  isActive: boolean;
};

export type HpeComponentDto = {
  hpComponentId: ID;
  uomId: ID;
  componentId: ID;
  code: string;
  name: string;
  memo: string;
  cost: number;
  isActive: boolean;
  extension: number;
  quantity: number;
  items: HpeComponentItemDto[];
  formula?: FormulaDto;
  oldFormula?: FormulaDto;
  topIds?: ID[];
  cuttingList?: HpeCuttingListItemDto[];
};

export type HpeDetailsDto = WithVersion<WithOptimisticLock<{
  housePlanId: ID;
  code: string;
  name: string;
  cost: number;
  viewMode: HpeViewMode;
  hpTops: HpeTopDto[];
  hpCostCenters: HpeCostCenterRefDto[];
  hpComponents: HpeComponentDto[];
  tops: HpeTopRefItemDto[];
  uoms: HpeUomRefItemDto[];
  masterZoneHouseEstimatePlansFolderId: ID;
  memo?: string;
}>>;

export type HpeDetailsSaveRequestCostCenterDto = WithVersion<WithDirtyType<{
  costCenterId: ID;
  hpCostCenterId?: ID;
}>>;

export type HpeDetailsSaveRequestComponentDto = WithDirtyType<{
  componentId: ID;
  quantity: number;
  hpComponentId?: ID;
  formula?: FormulaDto;
  cuttingList?: HpeCuttingListItemDto[];
}>;

export type HpeDetailsSaveRequestItemDto = WithDirtyType<{
  itemId: ID;
  costCenterId: ID;
  quantity: number;
  memo: string;
  isActive: boolean;
  hpItemId?: ID;
  formula?: FormulaDto;
  cuttingList?: HpeCuttingListItemDto[];
}>;

export type HpeDetailsSaveRequestTopDto = {
  topId: ID;
  value: Quantity<any>;
  count: number;
  isFromGto: boolean;
};

export type HpeDetailsSaveRequestDto = {
  housePlanId: ID;
  isSave: boolean;
  hpCostCenters: HpeDetailsSaveRequestCostCenterDto[];
  hpComponents: HpeDetailsSaveRequestComponentDto[];
  hpItems: HpeDetailsSaveRequestItemDto[];
  hpTops: HpeDetailsSaveRequestTopDto[];
  priceBookId?: ID;
  memo?: string;
};

export type HpeGetCostCenterRequestCostCenterDto = WithVersion<WithDirtyType<{
  costCenterId: ID;
  hpCostCenterId?: ID;
}>>;

export type HpeGetCostCenterRequestComponentDto = WithDirtyType<{
  componentId: ID;
  quantity: number;
  formula: FormulaDto;
  isActive: boolean;
  hpComponentId?: ID;
  cuttingList?: HpeCuttingListItemDto[];
}>;

export type HpeGetCostCenterRequestItemDto = WithDirtyType<{
  itemId: ID;
  costCenterId: ID;
  quantity: number;
  formula: FormulaDto;
  memo: string;
  isActive: boolean;
  hpItemId?: ID;
  cuttingList?: HpeCuttingListItemDto[];
}>;

export type HpeGetCostCenterRequestTopDto = WithDirtyType<{
  topId: ID;
  value: Quantity<any>;
  count: number;
}>;

export type HpeGetCostCenterRequestDto = {
  hpCostCenters: HpeGetCostCenterRequestCostCenterDto[];
  hpComponents: HpeGetCostCenterRequestComponentDto[];
  hpItems: HpeGetCostCenterRequestItemDto[];
  hpTops: HpeGetCostCenterRequestTopDto[];
};

export type HpeGetCostCenterResponseSalesTaxDto = {
  code: string;
  name: string;
  cost: number;
  uomId: ID;
  quantity: number;
  extension: number;
};

export type HpeGetCostCenterResponseItemDto = {
  hpItemId: ID;
  itemId: ID;
  code: string;
  name: string;
  isSalesTax: boolean;
  quantity: number;
  cost: number;
  uomId: ID;
  extension: number;
  isActive: boolean;
  formula?: FormulaDto;
  oldFormula?: FormulaDto;
  cuttingList?: HpeCuttingListItemDto[];
  topIds?: ID[];
  memo?: string;
  hpComponentId?: ID;
};

export type HpeGetCostCenterResponseDto = WithVersion<{
  code: string;
  name: string;
  cost: number;
  items: HpeGetCostCenterResponseItemDto[];
  salesTax?: HpeGetCostCenterResponseSalesTaxDto;
  memo?: string;
}>;

export type HpeGetComponentsResponseDto = {
  components: HpeComponentDto[];
  affectedCostCenters: HpeCostCenterRefDto[];
};

export type HpeCcOrderDetailsDto = {
  code: string;
  name: string;
  cost: number;
  items: HpeCcOrderDetailsItemDto[];
};

export type HpeCcOrderDetailsItemDto = {
  code: string;
  cost: number;
  extension: number;
  quantity: number;
};

export type HpeGenerateItemIdsResponseDto = { hpItemIds: ID[] };

export const ComponentPropagateFormulaMode = createEnum('ITEMS_AND_COMPONENTS_FINDER', {
  Items: '1',
  Components: '2',
  All: '3',
});

export type ComponentPropagateFormulaMode = EnumSet<typeof ComponentPropagateFormulaMode>;

export type ComponentPropagateFormulaRequestDto = WithOptimisticLock<{
  includesIds?: ID[];
  excludesIds?: ID[];
  oldFormula?: FormulaDto;
  newFormula?: FormulaDto;
  costCenterId?: ID;
  itemId?: ID;
  componentId?: ID;
  targetHousePlans?: SelectionModel;
}>;

export type ComponentPropagateFormulaModelItemDto = {
  id: ID;
  code: string;
  name: string;
  cost: number;
  uomId: ID;
  uomCode: string;
  active: boolean;
  extension: number;
  isSalesTax: boolean;
  component: boolean;
  formula?: FormulaDto;
  topIds?: ID[];
  memo?: string;
};

export type ComponentPropagateFormulaModelItemCustomDto = WithVersion<{
  id: ID;
  officeId: ID;
  code: string;
  name: string;
  cost: number;
  style: number;
  active: boolean;
  pictureId: number;
  params: boolean;
  addeDate: Date;
  addedBy: number;
  updatedDate: Date;
  updatedBy: number;
}>;

export type ComponentPropagateFormulaRequestCustomDto = { showDeleted: boolean };
